import { useCallback, useEffect, useState } from 'react';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  uploadSettlementDocument,
  type SettlementDocumentListItem,
} from '../utils/settlementDocumentsClient';
import { DEATH_CERT_API_PHASE, DEATH_CERT_STEP_ID } from '../utils/deathCertWorkflow';

export interface DeathCertUiDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
}

function toUi(d: SettlementDocumentListItem): DeathCertUiDocument {
  return {
    id: d.id,
    name: d.filename,
    type: d.mimeType,
    size: d.size,
    uploadedAt: d.createdAt,
  };
}

/**
 * Settlement documents for the death certificate IAD step, filtered by description prefix.
 */
export function useDeathCertDocuments(estateId: string | undefined, descriptionPrefix: string) {
  const [documents, setDocuments] = useState<DeathCertUiDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!estateId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const all = await listSettlementDocuments(estateId, DEATH_CERT_API_PHASE, DEATH_CERT_STEP_ID);
      setDocuments(
        all
          .filter((d) => (d.description || '').startsWith(descriptionPrefix))
          .map(toUi)
      );
    } catch (e) {
      console.error('[useDeathCertDocuments] list failed', e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [estateId, descriptionPrefix]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onUpload = useCallback(
    async (file: File, exactDescription: string) => {
      if (!estateId) throw new Error('Missing estate');
      await uploadSettlementDocument(estateId, DEATH_CERT_API_PHASE, DEATH_CERT_STEP_ID, file, exactDescription);
      await refresh();
    },
    [estateId, refresh]
  );

  const onDelete = useCallback(
    async (docId: string) => {
      if (!estateId) return;
      await deleteSettlementDocument(estateId, docId);
      await refresh();
    },
    [estateId, refresh]
  );

  return { documents, loading, refresh, onUpload, onDelete };
}
